import { useCallback } from 'react';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import {
	type Recommendation,
	useReadyToShareContext,
	useSharingModal,
} from '#src/app/pages/starterDashboardV2/shared/contexts';
import type { RootShareRecommendations } from '#src/app/pages/starterDashboardV2/shared/hooks';

import { useShareRecommendationsTracking } from '../../../hooks/useShareRecommendationsTracking';

type Platform = keyof RootShareRecommendations;

type UseSocialGroupCardClickProps = {
	platform: Platform;
	viewContext: 'home' | 'share';
};

type UseSocialGroupCardClickReturn = {
	handleLinkClick: (recommendation: Recommendation, location: string, context: string) => void;
};

function openUrlInNewTab(url: string) {
	window.open(url, '_blank', 'noopener,noreferrer');
}

export function useSocialGroupCardClick({
	platform,
	viewContext,
}: UseSocialGroupCardClickProps): UseSocialGroupCardClickReturn {
	const {
		actions: { handleLinkClick: trackLinkClick },
	} = useShareRecommendationsTracking();
	const {
		data: {
			petition,
			viewer: {
				experiments: { starterExperienceReadyToSharePredrafted },
			},
		},
	} = useStarterDashboardV2PageData();
	const { startSharingFlow } = useSharingModal();
	const {
		data: { postTypeData },
	} = useReadyToShareContext();

	const handleLinkClick = useCallback(
		(rec: Recommendation, location: string, context: string) => {
			trackLinkClick(rec.name, location, rec.metadata.source, context);
			if (starterExperienceReadyToSharePredrafted.variation === 'enabled') {
				const post =
					platform === 'facebook'
						? postTypeData.FACEBOOK_GROUPS_POST?.posts[0]
						: postTypeData.REDDIT_GROUPS_POST?.posts[0];

				if (!post) {
					openUrlInNewTab(rec.url);
					return;
				}

				startSharingFlow({
					post,
					petition,
					recommendedGroup: rec,
					trackingProps: {
						context: 'petition_dashboard_group_rec',
						viewContext,
					},
				});
			} else {
				openUrlInNewTab(rec.url);
			}
		},
		[
			platform,
			trackLinkClick,
			startSharingFlow,
			viewContext,
			petition,
			postTypeData,
			starterExperienceReadyToSharePredrafted.variation,
		],
	);

	return {
		handleLinkClick,
	};
}
